<script setup>

import { ref, computed, inject, watch } from 'vue';
import { 
	ENCODAGES_FICHIER,
	DELIMITEURS_FICHIER,
	SEPARATEURS_DECIMAUX } from '@/scripts/global.js';
import { 
	apiModifierInfosImport,
	apiSupprimerImports } from '@/services/ServiceModele.js';
import FormuleAffichee from '@/components/generiques/FormuleAffichee.vue';
import Dropdown from 'primevue/dropdown';
import Tag from 'primevue/tag';

const { importDonnees } = defineProps({
  importDonnees: Object
});

const appelerApiModifModele = inject('appelerApiModifModele');
const afficherBarreFlottante = inject('afficherBarreFlottante');
const ouvrirFenetreFormule = inject('ouvrirFenetreFormule');

const NB_COLONNES_AFFICHEES = 5;

const infosImport = ref(getInfosImport(importDonnees));
const boutonMenu = ref(null);

const premieresColonnes = computed(() => {
	if (importDonnees.nomsColonnes.length <= NB_COLONNES_AFFICHEES) {
		return importDonnees.nomsColonnes;
	} else {
		const premieresColonnes = importDonnees.nomsColonnes.slice(0, NB_COLONNES_AFFICHEES);
		premieresColonnes.push('...');
		return premieresColonnes;
	}
});

watch(() => getInfosImport(importDonnees), (importDonnees) => {
	infosImport.value = importDonnees;
});

const itemsMenu = [
	{
		libelle: 'Supprimer',
		icones: ['fa fa-trash'],
		style: 'color: var(--couleur-supprimer); ',
		commande: () => {
			supprimer();
		}
	}
];

function getInfosImport(importDonnees) {
	return {
		nom: importDonnees?.nom,
		libelle: importDonnees?.libelle,
		encodage: ENCODAGES_FICHIER.find(encodage => encodage.chaine === importDonnees?.encodage),
		delimiteur: DELIMITEURS_FICHIER.find(delim => delim.chaine === importDonnees?.delimiteur),
		separateurDecimal: SEPARATEURS_DECIMAUX.find(sep => sep.chaine === importDonnees?.separateurDecimal),
		formatDates: importDonnees?.formatDates,
		formatBooleens: importDonnees?.formatBooleens,
		delimiteurListe: DELIMITEURS_FICHIER[3]
	};
}

function basculerAffichageMenu() {
    afficherBarreFlottante({
        items: itemsMenu, 
        selectionItems: [],
        composantParent: boutonMenu.value
    });
}

function afficherFormule() {
	ouvrirFenetreFormule(importDonnees);
}

function reinitialiserValeurs() {
	infosImport.value = getInfosImport(importDonnees);
}

function modifierInfosImport() {
	const importDTO = {
		nom: infosImport.value.nom,
		libelle: infosImport.value.libelle,
		encodage: infosImport.value.encodage.chaine,
		delimiteur: infosImport.value.delimiteur.chaine,
		separateurDecimal: infosImport.value.separateurDecimal.chaine,
		formatDates: infosImport.value.formatDates,
		formatBooleens: infosImport.value.formatBooleens,
		delimiteurListe: infosImport.value.delimiteurListe.chaine
	};
	appelerApiModifModele(apiModifierInfosImport, importDonnees.id, importDTO);
}

function supprimer() {
	appelerApiModifModele(apiSupprimerImports, [importDonnees]);
}

</script>

<template>
	<div class="detail-import">
		<label class="intitule">Nom :</label>
		<div class="zone-nom">
			<input type="text" class="saisie-nom" spellcheck="false" 
				v-model="infosImport.nom" 
				@keyup.enter="modifierInfosImport()"
				@blur="reinitialiserValeurs" />
			<button class="bouton fa-solid fa-caret-down" 
				ref="boutonMenu"
				@click.stop="basculerAffichageMenu" />
		</div>
		<label class="intitule">Libellé :</label>
		<input type="text" spellcheck="false" 
			v-model="infosImport.libelle" 
			@keyup.enter="modifierInfosImport()"
			@blur="reinitialiserValeurs" />
		<label class="intitule">Encodage :</label>
		<Dropdown 
            v-model="infosImport.encodage" 
            :options="ENCODAGES_FICHIER" 
            :optionLabel="'libelle'"
            :placeholder="'Indéfini'"
            @update:modelValue="modifierInfosImport" />
		<label class="intitule">Délimiteur :</label>
		<Dropdown 
            v-model="infosImport.delimiteur" 
            :options="DELIMITEURS_FICHIER" 
            :optionLabel="'libelle'"
            :placeholder="'Indéfini'"
            @update:modelValue="modifierInfosImport" />
		<label class="intitule">Séparateur déc. :</label>
		<Dropdown 
            v-model="infosImport.separateurDecimal" 
            :options="SEPARATEURS_DECIMAUX" 
            :optionLabel="'libelle'"
            :placeholder="'Indéfini'"
            @update:modelValue="modifierInfosImport" />
		<label class="intitule">Format dates :</label>
		<input type="text" spellcheck="false" 
			v-model="infosImport.formatDates" 
			@keyup.enter="modifierInfosImport()"
			@blur="reinitialiserValeurs" />
		<label class="intitule">Format bool. :</label>
		<input type="text" spellcheck="false" 
			v-model="infosImport.formatBooleens" 
			@keyup.enter="modifierInfosImport()"
			@blur="reinitialiserValeurs" />
		<label class="intitule">Colonnes :</label>
		<div class="liste-badges" tabindex="0" @click="afficherFormule()">
			<label v-if="importDonnees.nomsColonnes.length === 0" class="aucun"><i>Aucune</i></label>
			<Tag v-for="nomColonne in premieresColonnes" class="badge nom-colonne">
				{{ nomColonne }}
			</Tag>
		</div>
		<label class="intitule">Formule :</label>
		<template v-if="importDonnees.formule">
			<div
				class="zone-apercu-formule"
				:class="{'formule-en-erreur': importDonnees.formuleEnErreur}"
				@click="ouvrirFenetreFormule(importDonnees)">
				<FormuleAffichee 
					:formule="importDonnees?.formule" 
					class="apercu-formule" />
			</div>
		</template>
	</div>
</template>

<style scoped>

.detail-import {
	display: grid;
	grid-template-columns: auto minmax(0, 1fr);
	gap: 2px;
	margin-top: 10px;
	margin-bottom: 20px;
	padding-left: 5px;
	align-items: start;
}

.zone-nom {
	display: flex;
	flex-direction: row;
	justify-content: space-between;
}

.saisie-nom {
	margin-right: 2px;
	flex-grow: 1;
	min-width: 20px;
}

.liste-badges {
	border: solid 1px var(--couleur-bordure);
	border-radius: 2px;
	padding: 2px;
	user-select: none;
	background-color: white;
}

.liste-badges:hover {
	border-color: var(--couleur-survol);
	cursor: pointer;
}

.badge {
	border: solid 1px var(--couleur-bordure);
	border-radius: 5px;
	user-select: none;
	margin: 0px 2px;
	padding: 5px 7px;
	font-weight:700 !important;
	font-size: 7px !important;
}

.aucun {
	cursor: pointer;
}

.nom-colonne {
	background-color: var(--couleur-colonne) !important;
	margin: 0px 1px;
	color: black;
}

</style>